/**
 * Booking Detail Header
 *
 * Charter: documentation/cursor/Charters/CodeAuditor.md v1.0.1
 *
 * Purpose:
 *   Presentational header for the Booking Detail Modal: guest name, reservation id,
 *   channel display + color badge, dates/nights/guests, and Close button.
 *
 * Assumptions:
 *   - All display values are pre-computed by the parent (BookingDetailModal).
 *   - No hooks, effects, or API calls. Pure presentational only.
 *
 * Dependencies:
 *   - None (React only). Receives styles and all computed props from parent.
 *
 * Version: 1.0.0
 *
 * Changelog:
 *   2026-01-27 Phase 6b: extracted from BookingDetailModal.jsx; JSX moved verbatim.
 */

function BookingDetailHeader({
  theme,
  onClose,
  styles,
  guestDisplayName,
  initials,
  reservationId,
  channelCode,
  sourceColor,
  sourceChannelDisplay,
  checkinDate,
  checkoutDate,
  nights,
  guestCount,
  showDoneButton = false,
}) {
  return (
    <div style={{
      ...styles.header,
      backgroundColor: theme === 'dark' ? 'var(--color-bg-primary-dark)' : 'var(--color-bg-primary)',
    }}>
      <div style={styles.headerTop}>
        <div style={styles.headerLeft}>
          <div style={styles.headerTitleRow}>
            <div style={{
              ...styles.monogram,
              backgroundColor: theme === 'dark' ? 'var(--color-bg-secondary-dark)' : 'var(--color-bg-secondary)',
              color: theme === 'dark' ? 'var(--color-text-primary-dark)' : 'var(--color-text-primary)',
            }}>
              {initials}
            </div>
            <div style={styles.headerTitleStack}>
              {/* Line 1: Guest Name · Reservation # · Channel */}
              <div style={{
                ...styles.headerPrimaryLine,
                color: theme === 'dark' ? 'var(--color-text-primary-dark)' : 'var(--color-text-primary)',
              }}>
                <span style={{ fontWeight: 'var(--font-weight-semibold)' }}>
                  {guestDisplayName}
                </span>
                {reservationId && (
                  <>
                    <span style={{ margin: '0 8px', color: theme === 'dark' ? 'var(--color-text-secondary-dark)' : 'var(--color-text-secondary)' }}>·</span>
                    <span style={{ fontWeight: 'var(--font-weight-semibold)' }}>Reservation #{reservationId}</span>
                  </>
                )}
                {channelCode && (
                  <>
                    <span style={{ margin: '0 8px', color: theme === 'dark' ? 'var(--color-text-secondary-dark)' : 'var(--color-text-secondary)' }}>·</span>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', opacity: 0.8 }}>
                      <span style={{
                        display: 'inline-block',
                        width: '6px',
                        height: '6px',
                        borderRadius: '50%',
                        backgroundColor: sourceColor || 'var(--color-text-secondary)',
                        flexShrink: 0,
                      }} />
                      <span>{sourceChannelDisplay}</span>
                    </span>
                  </>
                )}
              </div>
              {/* Line 2: Check-in → Check-out · Nights · Guests */}
              <div style={{
                ...styles.headerSecondaryLine,
                color: theme === 'dark' ? 'var(--color-text-secondary-dark)' : 'var(--color-text-secondary)',
              }}>
                {checkinDate && checkoutDate && (
                  <>
                    <span>{checkinDate}</span>
                    <span style={{ margin: '0 6px' }}>→</span>
                    <span>{checkoutDate}</span>
                  </>
                )}
                {nights && (
                  <>
                    <span style={{ margin: '0 8px' }}>·</span>
                    <span style={{ opacity: 0.7 }}>{nights} {nights === 1 ? 'night' : 'nights'}</span>
                  </>
                )}
                {guestCount && (
                  <>
                    <span style={{ margin: '0 8px' }}>·</span>
                    <span style={{ opacity: 0.7 }}>{guestCount} {guestCount === 1 ? 'guest' : 'guests'}</span>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
        <div style={{
          ...styles.headerActions,
          alignItems: 'flex-start',
          paddingTop: '2px',
          display: 'flex',
          gap: '8px',
        }}>
          {showDoneButton && (
            <button
              type="button"
              style={{
                border: 'none',
                background: 'transparent',
                cursor: 'pointer',
                padding: '8px 12px',
                borderRadius: '6px',
                fontSize: 'var(--font-size-base)',
                fontWeight: 'var(--font-weight-medium)',
                color: 'var(--color-brand-primary, #2563eb)',
              }}
              onClick={onClose}
              aria-label="Done"
            >
              Done
            </button>
          )}
          <button
            style={{
              ...(styles.closeButtonRefined || {}),
              border: 'none',
              background: 'transparent',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '8px',
              borderRadius: '6px',
              transition: 'opacity 0.2s ease, background-color 0.2s ease',
              opacity: 0.7,
            }}
            onClick={onClose}
            aria-label="Close"
            onMouseEnter={(e) => {
              e.currentTarget.style.opacity = '1';
              e.currentTarget.style.backgroundColor = theme === 'dark'
                ? 'rgba(255, 255, 255, 0.1)'
                : 'rgba(0, 0, 0, 0.05)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.opacity = '0.7';
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
            onFocus={(e) => {
              e.currentTarget.style.outline = '2px solid var(--color-primary-base)';
              e.currentTarget.style.outlineOffset = '2px';
              e.currentTarget.style.opacity = '1';
            }}
            onBlur={(e) => {
              e.currentTarget.style.outline = 'none';
              e.currentTarget.style.opacity = '0.7';
            }}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              style={{
                stroke: theme === 'dark' ? 'var(--color-text-primary-dark)' : 'var(--color-text-primary)',
                strokeWidth: '1.5',
                strokeLinecap: 'round',
                strokeLinejoin: 'round',
              }}
            >
              <path d="M5 5 L15 15 M15 5 L5 15" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}

export default BookingDetailHeader;
